import { useEffect, useState } from "react";
import axios from "axios";
import DOMPurify from "dompurify";
import { API } from "../App";

/**
 * PageBlocks — drop-in component for landing pages.
 *
 * Reads VISIBLE rich-text blocks from /api/public/page-blocks/{pageKey} and
 * renders them inline. Mount once per placement slot (top / middle / bottom):
 *
 *   <PageBlocks pageKey="beta_launch" placement="top" />
 *
 * Server already strips <script>/<iframe>/on*= handlers; we sanitise again in
 * the browser via DOMPurify before injecting HTML. New blocks added in the
 * Founder CMS show up here automatically — no redeploy needed.
 */
const PageBlocks = ({ pageKey, placement, className = "", refreshKey = 0 }) => {
  const [blocks, setBlocks] = useState([]);

  useEffect(() => {
    let alive = true;
    const params = placement ? `?placement=${placement}` : "";
    axios.get(`${API}/public/page-blocks/${pageKey}${params}`)
      .then(r => { if (alive) setBlocks(r.data || []); })
      .catch(() => { if (alive) setBlocks([]); });
    return () => { alive = false; };
  }, [pageKey, placement, refreshKey]);

  if (!blocks.length) return null;

  return (
    <div className={`page-blocks-region ${className}`} data-testid={`page-blocks-${pageKey}-${placement || "all"}`}>
      {blocks.map((b) => (
        <article
          key={b.block_id}
          className="page-block-item prose prose-sm sm:prose max-w-none text-[#475569] [&_h1]:text-[#6B4E8A] [&_h2]:text-[#6B4E8A] [&_h3]:text-[#6B4E8A] [&_a]:text-[#6B4E8A] [&_a]:underline [&_strong]:text-[#6B4E8A] [&_ul]:list-disc [&_ol]:list-decimal [&_li]:ml-5"
          data-testid={`page-block-${b.block_id}`}
          // Sanitised on the server (strip <script>, <iframe>, on*= handlers)
          // AND again here by DOMPurify before injection.
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(b.content_html, {
              ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "s", "h1", "h2", "h3", "h4", "ul", "ol", "li", "blockquote", "a", "span", "div", "hr"],
              ALLOWED_ATTR: ["href", "target", "rel", "class", "style"],
            }),
          }}
        />
      ))}
    </div>
  );
};

export default PageBlocks;
